import { useEffect, useState } from 'react';
import he from 'he';

const PUBLICATIONS_ENDPOINT =
  'https://blog.medbioinformatics.com/wp-json/medbio/v1/publications-all';
const DISGENET_TAG = 'DISGENET';

interface PublicationsApiItem {
  id: number;
  title: string;
  date: string;
  publication_type?: string;
  publication_tags?: string[];
  journal?: string;
  doi?: string;
  link?: string;
}

export interface PublicationCardItem {
  id: number;
  title: string;
  date: string;
  publicationType: string;
  journal: string;
  doi: string;
  doiUrl: string | null;
  externalLink: string | null;
}

const stripHtml = (value: string) => value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const formatYear = (value: string) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return String(parsed.getFullYear());
};

const normalizeDoiUrl = (doi: string) => {
  const trimmed = doi.trim();
  if (!trimmed) return null;
  const withoutPrefix = trimmed.replace(/^https?:\/\/(dx\.)?doi\.org\//i, '').replace(/^\//, '');
  const href = `https://doi.org/${withoutPrefix}`;
  try {
    return new URL(href).toString();
  } catch {
    return null;
  }
};

const getValidHttpsUrl = (value?: string | null) => {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'https:') return null;
    return parsed.toString();
  } catch {
    return null;
  }
};

const hasDisgenetTag = (tags?: string[]) =>
  tags?.some(tag => tag.toUpperCase() === DISGENET_TAG) ?? false;

export const usePublications = (enabled: boolean) => {
  const [publications, setPublications] = useState<PublicationCardItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!enabled || loaded) return;

    const fetchPublications = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(PUBLICATIONS_ENDPOINT);
        if (!response.ok) {
          throw new Error('Publications request failed');
        }
        const parsed: unknown = await response.json();
        if (!Array.isArray(parsed)) {
          throw new Error('Unexpected publications response');
        }

        const disgenetOnly = (parsed as PublicationsApiItem[])
          .filter(item => hasDisgenetTag(item.publication_tags))
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        const mapped = disgenetOnly.map(item => {
          const rawTitle = item.title || 'Untitled';
          const decodedTitle = he.decode(stripHtml(rawTitle));
          const doi = (item.doi || '').trim();
          const doiUrl = normalizeDoiUrl(doi);
          const externalLink = getValidHttpsUrl(item.link || null);

          return {
            id: item.id,
            title: decodedTitle,
            date: formatYear(item.date),
            publicationType: stripHtml(item.publication_type || ''),
            journal: stripHtml(item.journal || ''),
            doi,
            doiUrl,
            externalLink,
          };
        });

        setPublications(mapped);
      } catch (fetchError) {
        setError(fetchError instanceof Error ? fetchError.message : 'Failed to load publications');
      } finally {
        setLoading(false);
        setLoaded(true);
      }
    };

    fetchPublications();
  }, [enabled, loaded]);

  return { publications, loading, error };
};
